/* eslint-disable jsx-a11y/anchor-has-content */
import { useEffect, useState } from "react";
import type { ChangeEvent } from "react";
import { useLocalization, Localized } from "@fluent/react";
import { RadioButtons } from "@narmi/design_system";
import {
  ContextForm,
  PreferenceContentCard,
  useFormData,
  useLoadingContext,
  useNotificationContext
} from "cerulean";
import Account from "byzantine/src/Account";
import ApiHttp from "byzantine/src/ApiHttp";
import type User from "byzantine/src/User";
import AddMfaDevice from "./AddMfaDevice";
import AddTotpMfaDevice from "./AddTotpMfaDevice/AddTotpMfaDevice";
import EstatementsManager from "../estatements/EstatementsManager";
import SubmitPreferences from "./SubmitPreferences";

type AutomaticSavingsEnrollment = {
  automatic_savings_active?: boolean | null;
  automatic_savings_eligible?: boolean;
  source_account?: API.AnyAccount;
  destination_account?: API.AnyAccount;
};

type SetUpYourAccountPreferencesProps = {
  user: User;
  institution: API.Institution;
};

const SetUpYourAccountPreferences = ({
  user,
  institution
}: SetUpYourAccountPreferencesProps) => {
  const { mfa_required: mfaRequired } = institution;
  const enrollmentMfaMethods = institution.enrollment_mfa_methods ?? ["phone"];
  const hasPhoneMfa = enrollmentMfaMethods.includes("phone");
  const hasAppMfa = enrollmentMfaMethods.includes("app");
  const hasMethodChoice = hasPhoneMfa && hasAppMfa;
  const [selectedMfaMethod, setSelectedMfaMethod] = useState<"phone" | "app">(
    hasPhoneMfa ? "phone" : "app"
  );
  // This prevents us from requesting QR code data unless they've explicitly requested it
  const [hasVisitedAppSetup, setHasVisitedAppSetup] = useState(
    !hasPhoneMfa && hasAppMfa
  );
  const { l10n } = useLocalization();
  const [automaticSavingsError, setAutomaticSavingsError] = useState<unknown>();
  const [automaticSavingsAccounts, setAutomaticSavingsAccounts] =
    useState<AutomaticSavingsEnrollment>({});
  const [hasAddedOrReverifiedDevice, setHasAddedOrReverifiedDevice] =
    useState(false);
  const { setIsLoading } = useLoadingContext();
  const { sendNotification } = useNotificationContext();

  const { formData, onChange } = useFormData<{
    automaticSavings?: boolean;
    twoFactorAuthentication?: { phone_number?: string; token?: string };
  }>({});

  useEffect(() => {
    setIsLoading(true);
    ApiHttp.fetch<AutomaticSavingsEnrollment>("automatic_savings_enrollment")
      .then((res) => {
        setAutomaticSavingsAccounts(res);
        if (res?.automatic_savings_active) {
          onChange({ automaticSavings: true });
        }
      })
      .catch((err) => setAutomaticSavingsError(err))
      .finally(() => setIsLoading(false));
  }, []);

  // default to on if FI has OPT_OUT, user is eligible & personal, and automatic_savings has never been enabled before
  useEffect(() => {
    if (
      institution.features?.automatic_savings_in_enrollment &&
      !user.isBusiness() &&
      automaticSavingsAccounts?.automatic_savings_active === null &&
      automaticSavingsAccounts?.automatic_savings_eligible &&
      institution.features?.automatic_savings === "OPT_OUT"
    ) {
      ApiHttp.fetch("automatic_savings_enrollment", { method: "POST" }).then(
        () => {
          onChange({ automaticSavings: true });
        }
      );
    }
  }, [automaticSavingsAccounts?.automatic_savings_eligible]);

  useEffect(() => {
    // users who have completed enrollment can simply be redirected
    if (user.is_converting === false) {
      setIsLoading(true);
      window.location.href = "/dashboard";
    }
  }, [user]);

  const sourceAccountDescription = automaticSavingsAccounts?.source_account
    ? Account.deserialize(
        automaticSavingsAccounts?.source_account
      ).getShortDescription(false)
    : "";
  const destinationAccountDescription =
    automaticSavingsAccounts?.destination_account
      ? Account.deserialize(
          automaticSavingsAccounts?.destination_account
        ).getShortDescription(false)
      : "";
  const automaticSavingsSubtitle = (
    <Localized
      id="automatic-savings-terms"
      vars={{ sourceAccountDescription, destinationAccountDescription }}
    >
      {`With automatic savings, we'll automatically transfer 3% of any external
      transfers/deposits from your ${sourceAccountDescription} into your ${destinationAccountDescription}`}
    </Localized>
  );

  const shouldRenderAutomaticSavings = () =>
    !automaticSavingsError &&
    institution.features?.automatic_savings_in_enrollment &&
    user?.features.automatic_savings &&
    user?.user_category === "personal" &&
    automaticSavingsAccounts?.automatic_savings_eligible;

  const submitAutomaticSavings = (val: boolean) => {
    // Both endpoints make either POST or DELETE requests to enroll/unenroll
    const httpMethod = val ? "POST" : "DELETE";
    ApiHttp.fetch("automatic_savings_enrollment", { method: httpMethod }).catch(
      (err) => {
        sendNotification({
          type: "negative",
          text: err
        });
        // even if an error is encountered, the toggle will still try to change, so set it back to where it was
        onChange({ automaticSavings: !val });
      }
    );
  };

  return (
    <div className="medium">
      <Localized id="heading-account-preferences">
        <h1>Set up your account preferences</h1>
      </Localized>
      <div className="margin--top--xs" data-testid="enrollment-page-subtitle">
        <Localized id="subheading-account-preferences"></Localized>
      </div>
      <ContextForm data={formData} onChange={onChange}>
        <div className="preferences-container">
          {hasMethodChoice && !hasAddedOrReverifiedDevice && (
            <fieldset className="enrollment-mfa-method-selector">
              <legend className="margin--bottom--s">
                {l10n.getString("enrollment-mfa-method-label")}
              </legend>
              <RadioButtons
                name="enrollment-mfa-method"
                value={selectedMfaMethod}
                options={{
                  [l10n.getString("enrollment-mfa-method-phone")]: "phone",
                  [l10n.getString("enrollment-mfa-method-app")]: "app"
                }}
                onChange={(event: ChangeEvent<HTMLInputElement>) => {
                  const method = event.target.value;
                  if (method !== "phone" && method !== "app") {
                    return;
                  }
                  setSelectedMfaMethod(method);
                  if (method === "app") setHasVisitedAppSetup(true);
                }}
              />
            </fieldset>
          )}
          <div>
            {hasAppMfa && hasVisitedAppSetup && (
              <div
                className="enrollment-mfa-panel"
                inert={selectedMfaMethod !== "app"}
              >
                <div className="enrollment-mfa-panel-content">
                  <div className="enrollment-mfa-app-slide">
                    <AddTotpMfaDevice
                      user={user}
                      onDeviceAdded={() => setHasAddedOrReverifiedDevice(true)}
                    />
                  </div>
                </div>
              </div>
            )}
            {hasPhoneMfa && (
              <div
                className="enrollment-mfa-panel"
                inert={selectedMfaMethod !== "phone"}
              >
                <div className="enrollment-mfa-panel-content">
                  <AddMfaDevice
                    mfa_required={mfaRequired}
                    user={user}
                    data={formData}
                    onChange={onChange}
                    setHasAddedOrReverifiedDevice={
                      setHasAddedOrReverifiedDevice
                    }
                  />
                </div>
              </div>
            )}
          </div>
          <EstatementsManager />
          {shouldRenderAutomaticSavings() && (
            <ContextForm.Field testId="automaticSavings">
              <PreferenceContentCard
                {...{ field: "automaticSavings" }}
                title={l10n.getString("label-automatic-savings")}
                subtitle={automaticSavingsSubtitle}
                subtext={l10n.getString("description-automatic-savings")}
                onChange={(val) => {
                  submitAutomaticSavings(val);
                }}
              />
            </ContextForm.Field>
          )}
        </div>
      </ContextForm>
      <SubmitPreferences
        institution={institution}
        mustAddOrReverifyDeviceOnSubmit={
          !hasAddedOrReverifiedDevice &&
          !user.has_permitted_device &&
          mfaRequired &&
          !institution?.permitted_mfa_methods?.includes("email") &&
          !user.has_backup_codes
        }
      />
    </div>
  );
};

export default SetUpYourAccountPreferences;
